<!--
  ChangelogVersionCard.vue
  
  Displays a complete version entry with badge and all change categories.
  Combines ChangelogVersionBadge and ChangelogChangeList for a single version.
  
  @example
  <ChangelogVersionCard
    :version="{ version: '1.2.3', date: '2025-11-15', added: [], changed: [], fixed: [] }"
    :is-latest="true"
  />
-->
<template>
  <div class="relative">
    <!-- Version Badge -->
    <ChangelogVersionBadge
      :version="version.version"
      :date="version.date"
      :is-latest="isLatest ?? false"
      class="mb-4"
    />

    <!-- Change Lists -->
    <div class="space-y-4">
      <ChangelogChangeList type="added" :items="version.added" />
      <ChangelogChangeList type="changed" :items="version.changed" />
      <ChangelogChangeList type="fixed" :items="version.fixed" />
    </div>
  </div>
</template>

<script setup lang="ts">
import ChangelogVersionBadge from './ChangelogVersionBadge.vue'
import ChangelogChangeList from './ChangelogChangeList.vue'

/**
 * Version information structure from parsed changelog
 */
interface VersionInfo {
  /** Semantic version string (e.g., "1.2.3") */
  version: string
  /** Release date in human-readable format */
  date: string
  /** List of new features added in this version */
  added: string[]
  /** List of changes/improvements in this version */
  changed: string[]
  /** List of bug fixes in this version */
  fixed: string[]
}

/**
 * Props for the ChangelogVersionCard component
 */
interface Props {
  /** Complete version information object */
  version: VersionInfo
  /** Whether this is the latest/current version */
  isLatest?: boolean
}

withDefaults(defineProps<Props>(), {
  isLatest: false,
})
</script>
